/**
 * IMPORTANT - BLOCKCHAIN DATA INTEGRITY:
 * This file handles blockchain data which must maintain exact values and case sensitivity.
 * - NEVER use toLowerCase() or toUpperCase()
 * - NEVER normalize strings or addresses
 * - NEVER transform collection names
 * - NEVER modify contract addresses
 * - ALWAYS use exact matches for comparisons
 * - ALWAYS preserve original data as it exists on-chain
 */

import { COLLECTION_ADDRESSES, CRYPTOPUNKS_ADDRESS, WPUNKS_ADDRESS } from '../shared/constants';
import { API_ENDPOINTS, RATE_LIMIT } from '../shared/api-config';

// Mock floor prices use exact collection names from constants
const MOCK_FLOOR_PRICES = Object.fromEntries(
  Object.entries(COLLECTION_ADDRESSES).map(([name, _]) => [
    name,
    { eth: 0, usd: 0 } // Initialize with zeros, actual values should come from API
  ])
);

// Detect test environment
const isTestEnvironment = typeof vi !== 'undefined' || process.env.NODE_ENV === 'test';

// Utility function for delay
const delay = ms => isTestEnvironment ? Promise.resolve() : new Promise(resolve => setTimeout(resolve, ms));

// Add jitter to delay
const addJitter = (delay) => {
    const jitter = delay * RATE_LIMIT.jitterFactor * (Math.random() - 0.5);
    return Math.max(1000, delay + jitter); // Ensure minimum 1s delay
};

// Track ongoing requests and failures
let activeRequests = 0;
let consecutiveFailures = 0;
const requestQueue = [];
const failedRequests = new Map();

// Process queue when a request completes
async function processQueue() {
    if (isTestEnvironment) {
        while (requestQueue.length > 0) {
            const { fn, resolve, reject } = requestQueue.shift();
            try {
                const result = await fn();
                resolve(result);
            } catch (error) {
                reject(error);
            }
        }
        return;
    }

    if (activeRequests >= RATE_LIMIT.concurrentLimit || requestQueue.length === 0) {
        return;
    }

    const { fn, resolve, reject } = requestQueue.shift();
    activeRequests++;

    try {
        const result = await fn();
        consecutiveFailures = 0;
        resolve(result);
    } catch (error) {
        consecutiveFailures++;
        reject(error);
    } finally {
        activeRequests--;
        // Add delay before processing next request if we had failures
        if (consecutiveFailures > 0) {
            await delay(addJitter(RATE_LIMIT.initialDelay * Math.pow(2, consecutiveFailures - 1)));
        }
        processQueue();
    }
}

// Queue a request for processing
async function queueRequest(fn) {
    return new Promise((resolve, reject) => {
        requestQueue.push({ fn, resolve, reject });
        processQueue();
    });
}

// Add Reservoir API key if needed
export const getApiConfig = () => ({
    headers: {
        'accept': '*/*'
    }
});

// Remove the normalizeCollectionName function as we should not transform blockchain data
export async function fetchCollectionId(collectionNameOrAddress) {
    // Validate input
    if (!collectionNameOrAddress) {
        throw new Error('Collection name or address is required');
    }

    // Check if input is an Ethereum address (including Art Blocks format)
    const isEthAddress = /^0x[a-fA-F0-9]{40}(?::\d+:\d+)?$/.test(collectionNameOrAddress);
    
    // If it's an Art Blocks style address, extract just the contract part
    const contractAddress = isEthAddress ? collectionNameOrAddress.split(':')[0] : collectionNameOrAddress;
    
    const url = `${API_ENDPOINTS.COLLECTIONS}?${isEthAddress ? 'contract' : 'name'}=${encodeURIComponent(contractAddress)}`;
    console.log('[Debug] Fetching collection data from:', url);
    
    const response = await fetch(url, getApiConfig());
    if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(`Failed to fetch collection data: ${response.status}${errorData.message ? ` - ${errorData.message}` : ''}`);
    }
    
    const data = await response.json();
    if (!data.collections || data.collections.length === 0) {
        throw new Error(`Collection not found: ${collectionNameOrAddress}`);
    }

    return data.collections[0];
}

export async function fetchFloorPrice(collectionNameOrAddress) {
    if (!collectionNameOrAddress) {
        console.error('No collection name or address provided');
        return null;
    }

    try {
        // Exact Ethereum address format check - case sensitive
        const isContractAddress = collectionNameOrAddress?.startsWith('0x') && collectionNameOrAddress.length === 42;
        
        const url = `${API_ENDPOINTS.COLLECTIONS}?${isContractAddress ? 'contract' : 'name'}=${encodeURIComponent(collectionNameOrAddress)}`;
        
        const response = await queueRequest(async () => {
            const res = await fetch(url, getApiConfig());
            if (!res.ok) {
                if (res.status === 429) {
                    const retryAfter = res.headers.get('retry-after');
                    await delay(retryAfter ? parseInt(retryAfter) * 1000 : RATE_LIMIT.initialDelay);
                    throw new Error('Rate limited, retrying...');
                }
                throw new Error(`API returned ${res.status}`);
            }
            return res.json();
        });

        if (!response.collections || !response.collections[0]?.floorAsk?.price) {
            console.error('No floor price data found');
            return null;
        }

        return {
            eth: parseFloat(response.collections[0].floorAsk.price.amount.native),
            usd: parseFloat(response.collections[0].floorAsk.price.amount.usd)
        };
    } catch (error) {
        console.error('Error fetching floor price:', error);
        return null;
    }
} 